import type { RecruitingNoteResponse } from '../../../../types/api';
import { NoteIcon } from '../../../../assets/NoteIcon';
import { EditIcon } from '../../../../assets/EditIcon';
import { useFreshRecruitingNoteUrls } from '../../../../hooks/useFreshRecruitingNoteUrls';
import { tourAnchor, type TourAnchorId } from '../../../tours/tourAnchors';
import './LatestNoteRow.css';

interface LatestNoteRowProps {
  note: RecruitingNoteResponse;
  onClick: () => void;
  /** Tour anchor for this row. A prop so only the first row in the feed carries it. */
  anchor?: TourAnchorId;
}

const PREVIEW_LIMIT = 180;

function formatRelativeTime(dateString: string): string {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function truncate(text: string, limit: number): string {
  if (!text) return '';
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trimEnd()}…`;
}

/**
 * A single row in the Latest Notes feed. Shows note type icon, content
 * preview (or thumbnail for handwritten notes), prospect name badge, and
 * a relative timestamp.
 */
export const LatestNoteRow = ({ note, onClick, anchor }: LatestNoteRowProps) => {
  const isHandwritten = note.noteType === 'handwritten';
  const timestamp = note.updatedAt ?? note.createdAt;
  const { imageUrl, isUnavailable, handleImageError } = useFreshRecruitingNoteUrls(note);

  return (
    <button
      type="button"
      className="latest-note-row"
      {...(anchor ? tourAnchor(anchor) : {})}
      onClick={onClick}
      aria-label={
        note.prospectName
          ? `Open ${note.prospectName}'s notes`
          : 'Open note'
      }
    >
      <div
        className={`latest-note-row__icon${
          isHandwritten ? ' latest-note-row__icon--handwritten' : ''
        }`}
        aria-hidden="true"
      >
        {isHandwritten ? <EditIcon className="latest-note-row__icon-svg" /> : <NoteIcon className="latest-note-row__icon-svg" />}
      </div>

      <div className="latest-note-row__body">
        {note.title && (
          <p className="latest-note-row__title">{note.title}</p>
        )}

        {isHandwritten && imageUrl && !isUnavailable ? (
          <img
            className="latest-note-row__thumbnail"
            src={imageUrl}
            alt="Handwritten note preview"
            onError={handleImageError}
          />
        ) : isHandwritten && imageUrl && isUnavailable ? (
          <div
            className="latest-note-row__thumbnail latest-note-row__thumbnail--unavailable"
            role="img"
            aria-label="Image unavailable"
          >
            Image unavailable
          </div>
        ) : (
          <p className="latest-note-row__preview">
            {truncate(note.content || 'Empty note', note.title ? 100 : PREVIEW_LIMIT)}
          </p>
        )}

        <div className="latest-note-row__meta">
          {note.prospectName && (
            <span className="latest-note-row__prospect-badge">{note.prospectName}</span>
          )}
          <span className="latest-note-row__timestamp">{formatRelativeTime(timestamp)}</span>
          {note.createdByName && (
            <span className="latest-note-row__author">· {note.createdByName}</span>
          )}
        </div>
      </div>
    </button>
  );
};
